import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { IconLayoutGrid, IconList, IconFlame, IconColorSwatch, IconPlus, IconSearch, IconX, IconSettings } from '@tabler/icons-react'
import NewPieceModal from '../components/NewPieceModal'
import PieceCard from './PieceCard'
import Bisqueware from './Bisqueware'
import Glazes from './Glazes'
import Settings from './Settings'

const THEMES = {
  'morning-glory': { header: '#2d6973', darkHeader: '#1e4f58', bg: '#f0f8f7', card: '#fff', border: '#c0e0dc', accent: '#fbc78d', accentDark: '#e8a060', accentText: '#5a2800', text: '#1a3a3e', textLight: '#5a8f96', textMuted: '#7bbfc9' },
  'orange-lichen': { header: '#b84a18', darkHeader: '#8c3210', bg: '#f5f0ea', card: '#fffaf4', border: '#e8d4b8', accent: '#f7c74b', accentDark: '#d4a020', accentText: '#3a2600', text: '#2e1a08', textLight: '#9a7050', textMuted: '#f0b898' },
  'blue-lichen': { header: '#3a3d58', darkHeader: '#262840', bg: '#f2f0f5', card: '#fefefe', border: '#d8d4e8', accent: '#d48d38', accentDark: '#b07020', accentText: '#fff0d8', text: '#1e1e30', textLight: '#6a6a88', textMuted: '#b0b8d8' },
  'peach-half-baked': { header: '#85cbcc', darkHeader: '#2d6973', bg: '#fdf8f0', card: '#fffdf8', border: '#f0dfc0', accent: '#fbc78d', accentDark: '#e8a060', accentText: '#5a2800', text: '#2a1e08', textLight: '#7a6040', textMuted: '#1e5058' },
  'cognac-teal': { header: '#7a3a18', darkHeader: '#5c2a10', bg: '#f7f0e6', card: '#fffaf2', border: '#e8d4b4', accent: '#3e8a84', accentDark: '#2d6973', accentText: '#e0f5f3', text: '#3a1e08', textLight: '#7a5030', textMuted: '#f0c8a0' },
}

const PIZZA_SVG = (color) => (
  <svg width="11" height="11" viewBox="0 0 28 28" fill="none">
    <path d="M14 4 L26 22 Q14 27 2 22 Z" stroke={color} strokeWidth="2.2" fill="none" strokeLinejoin="round"/>
    <path d="M14 4 Q20 13 26 22" stroke={color} strokeWidth="2" fill="none"/>
    <path d="M14 4 Q8 13 2 22" stroke={color} strokeWidth="2" fill="none"/>
    <path d="M6 19 Q14 24 22 19" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round"/>
    <circle cx="11" cy="15.5" r="1.8" fill={color}/>
    <circle cx="17" cy="13" r="1.8" fill={color}/>
  </svg>
)

const TEXTURES = [
  // 1: Speckled clay
  (t) => (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}}>
      <rect width="100%" height="100%" fill={t.border}/>
      <circle cx="20%" cy="30%" r="2" fill={t.header} opacity=".2"/>
      <circle cx="45%" cy="55%" r="1.5" fill={t.header} opacity=".18"/>
      <circle cx="70%" cy="25%" r="2.5" fill={t.darkHeader} opacity=".14"/>
      <circle cx="30%" cy="70%" r="1.5" fill={t.header} opacity=".18"/>
      <circle cx="80%" cy="65%" r="1" fill={t.darkHeader} opacity=".22"/>
      <circle cx="55%" cy="40%" r="3" fill={t.header} opacity=".1"/>
      <circle cx="15%" cy="55%" r="1.5" fill={t.darkHeader} opacity=".18"/>
      <circle cx="65%" cy="75%" r="2" fill={t.header} opacity=".15"/>
      <circle cx="88%" cy="38%" r="1.5" fill={t.darkHeader} opacity=".2"/>
      <circle cx="40%" cy="18%" r="2" fill={t.header} opacity=".14"/>
      <circle cx="72%" cy="50%" r="1" fill={t.header} opacity=".2"/>
      <circle cx="25%" cy="45%" r="2.5" fill={t.darkHeader} opacity=".12"/>
    </svg>
  ),
  // 2: Lichen blobs
  (t) => (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
      <rect width="100" height="100" fill={t.border}/>
      <ellipse cx="22" cy="20" rx="14" ry="9" fill={t.header} opacity=".2" transform="rotate(-15 22 20)"/>
      <ellipse cx="62" cy="38" rx="18" ry="10" fill={t.darkHeader} opacity=".15" transform="rotate(10 62 38)"/>
      <ellipse cx="82" cy="18" rx="10" ry="7" fill={t.header} opacity=".18" transform="rotate(20 82 18)"/>
      <ellipse cx="38" cy="65" rx="14" ry="8" fill={t.darkHeader} opacity=".16" transform="rotate(-8 38 65)"/>
      <ellipse cx="75" cy="72" rx="12" ry="7" fill={t.header} opacity=".18" transform="rotate(15 75 72)"/>
      <ellipse cx="12" cy="55" rx="8" ry="5" fill={t.darkHeader} opacity=".14" transform="rotate(-20 12 55)"/>
      <ellipse cx="90" cy="50" rx="9" ry="6" fill={t.header} opacity=".16" transform="rotate(5 90 50)"/>
    </svg>
  ),
  // 3: Watercolor wash
  (t) => (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}}>
      <defs>
        <radialGradient id="w1" cx="30%" cy="40%" r="60%">
          <stop offset="0%" stopColor={t.border} stopOpacity=".95"/>
          <stop offset="100%" stopColor={t.header} stopOpacity=".3"/>
        </radialGradient>
        <radialGradient id="w2" cx="75%" cy="65%" r="50%">
          <stop offset="0%" stopColor={t.border} stopOpacity=".8"/>
          <stop offset="100%" stopColor={t.darkHeader} stopOpacity=".25"/>
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill={t.border}/>
      <ellipse cx="30%" cy="40%" rx="60%" ry="55%" fill="url(#w1)" opacity=".8"/>
      <ellipse cx="72%" cy="62%" rx="50%" ry="45%" fill="url(#w2)" opacity=".7"/>
    </svg>
  ),
  // 4: Fine stipple
  (t) => (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}}>
      <defs>
        <pattern id="s" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
          <rect width="8" height="8" fill={t.border}/>
          <circle cx="1.5" cy="1.5" r=".7" fill={t.header} opacity=".2"/>
          <circle cx="5.5" cy="4" r=".5" fill={t.darkHeader} opacity=".16"/>
          <circle cx="3" cy="6.5" r=".7" fill={t.header} opacity=".18"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#s)"/>
    </svg>
  ),
  // 5: Throwing rings
  (t) => (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
      <rect width="100" height="100" fill={t.border}/>
      <circle cx="50" cy="50" r="10" fill="none" stroke={t.header} strokeWidth="1.5" opacity=".3"/>
      <circle cx="50" cy="50" r="20" fill="none" stroke={t.header} strokeWidth="1.2" opacity=".25"/>
      <circle cx="50" cy="50" r="30" fill="none" stroke={t.header} strokeWidth="1" opacity=".2"/>
      <circle cx="50" cy="50" r="42" fill="none" stroke={t.header} strokeWidth=".8" opacity=".18"/>
      <circle cx="50" cy="50" r="56" fill="none" stroke={t.header} strokeWidth=".7" opacity=".14"/>
      <circle cx="50" cy="50" r="72" fill="none" stroke={t.header} strokeWidth=".6" opacity=".1"/>
    </svg>
  ),
  // 6: Crosshatch
  (t) => (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}}>
      <defs>
        <pattern id="c" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
          <rect width="8" height="8" fill={t.border}/>
          <line x1="0" y1="8" x2="8" y2="0" stroke={t.header} strokeWidth=".6" opacity=".22"/>
          <line x1="0" y1="0" x2="8" y2="8" stroke={t.header} strokeWidth=".6" opacity=".22"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#c)"/>
    </svg>
  ),
]

const STAGE_LABELS = { 'greenware': 'Greenware', 'bisque-fire': 'Bisque-fire', 'glaze-fire': 'Glaze-fire', 'delivered': '🍕 Delivered' }

export default function Home({ session }) {
  const [pieces, setPieces] = useState([])
  const [pieceTypes, setPieceTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('grid')
  const [stageFilter, setStageFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('library')
  const [theme, setTheme] = useState('morning-glory')
  const [showNewPiece, setShowNewPiece] = useState(false)
  const [selectedPiece, setSelectedPiece] = useState(null)
  const [previousTab, setPreviousTab] = useState('library')
  const [showSettings, setShowSettings] = useState(false)
  const [ellipsisMenu, setEllipsisMenu] = useState(null)
  const t = THEMES[theme]

  useEffect(() => {
    fetchPieces()
    fetchPieceTypes()
  }, [])

  const fetchPieces = async () => {
    const { data, error } = await supabase
      .from('pieces')
      .select('*')
      .order('created_at', { ascending: false })
    if (!error) setPieces(data || [])
    setLoading(false)
  }

  const fetchPieceTypes = async () => {
    const { data } = await supabase.from('piece_types').select('*').order('name')
    if (data) setPieceTypes(data)
  }

  const filteredPieces = pieces.filter(p => {
    const matchStage = stageFilter === 'all' || p.firing_stage === stageFilter
    const matchType = typeFilter === 'all' || p.type_tag === typeFilter
    const matchSearch = !searchQuery || p.unique_id.toLowerCase().includes(searchQuery.toLowerCase())
    return matchStage && matchType && matchSearch
  })

  const getStagePillStyle = (stage) => {
    switch(stage) {
      case 'bisque-fire': return { background: t.accent, color: t.accentText }
      case 'glaze-fire': return { background: t.darkHeader, color: t.textMuted }
      case 'delivered': return { background: t.header, color: '#e8f8f6' }
      default: return { background: 'rgba(255,255,255,.85)', color: t.header, border: `1px solid ${t.border}` }
    }
  }

  const updatePieceStage = async (pieceId, stage) => {
    const updates = { firing_stage: stage }
    if (stage === 'bisque-fire') updates.bisque_date = new Date().toISOString().split('T')[0]
    if (stage === 'glaze-fire') updates.glaze_date = new Date().toISOString().split('T')[0]
    if (stage === 'delivered') updates.pickup_date = new Date().toISOString().split('T')[0]
    await supabase.from('pieces').update(updates).eq('id', pieceId)
    setPieces(prev => prev.map(p => p.id === pieceId ? { ...p, ...updates } : p))
    setEllipsisMenu(null)
  }

if (selectedPiece) return <PieceCard pieceId={selectedPiece} onBack={() => { setSelectedPiece(null); fetchPieces() }} theme={t} />
{ellipsisMenu && (
  <div onClick={() => setEllipsisMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 50 }}/>
)}

if (activeTab === 'bisqueware') return (
  <div style={{ fontFamily: 'Jost, sans-serif', background: t.bg, minHeight: '100vh', maxWidth: '430px', margin: '0 auto', position: 'relative', paddingBottom: '70px' }}>
    <Bisqueware theme={t} onSelectPiece={(id) => { setSelectedPiece(id); setPreviousTab('bisqueware') }} />
    <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '430px', background: t.card, borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-around', padding: '9px 0 18px', zIndex: 10 }}>
      {[
        { id: 'library', icon: <IconLayoutGrid size={20}/>, label: 'Library' },
        { id: 'bisqueware', icon: <IconFlame size={20}/>, label: 'Bisqueware' },
        { id: 'glazes', icon: <IconColorSwatch size={20}/>, label: 'Glazes' },
      ].map(item => (
        <div key={item.id} onClick={() => setActiveTab(item.id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: 'pointer', color: activeTab === item.id ? t.header : t.textLight }}>
          {item.icon}
          <span style={{ fontSize: '9px', fontWeight: '400' }}>{item.label}</span>
        </div>
      ))}
    </div>
  </div>
)

if (activeTab === 'glazes') return (
  <div style={{ fontFamily: 'Jost, sans-serif', background: t.bg, minHeight: '100vh', maxWidth: '430px', margin: '0 auto', position: 'relative', paddingBottom: '70px' }}>
    <Glazes theme={t} onSelectPiece={(id) => setSelectedPiece(id)} />
    <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '430px', background: t.card, borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-around', padding: '9px 0 18px', zIndex: 10 }}>
      {[
        { id: 'library', icon: <IconLayoutGrid size={20}/>, label: 'Library' },
        { id: 'bisqueware', icon: <IconFlame size={20}/>, label: 'Bisqueware' },
        { id: 'glazes', icon: <IconColorSwatch size={20}/>, label: 'Glazes' },
      ].map(item => (
        <div key={item.id} onClick={() => setActiveTab(item.id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: 'pointer', color: activeTab === item.id ? t.header : t.textLight }}>
          {item.icon}
          <span style={{ fontSize: '9px', fontWeight: '400' }}>{item.label}</span>
        </div>
      ))}
    </div>
  </div>
)

return (
    <div style={{ fontFamily: 'Jost, sans-serif', background: t.bg, minHeight: '100vh', maxWidth: '430px', margin: '0 auto', position: 'relative', paddingBottom: '70px' }}>

      {/* HEADER */}
      <div style={{ background: t.header, padding: '48px 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2px' }}>
          <div>
            <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '34px', fontWeight: '700', color: '#e8f8f6', lineHeight: 1 }}>Fired Up</div>
            <div style={{ fontSize: '10px', fontWeight: '300', color: t.textMuted, marginBottom: '10px', marginTop: '2px' }}>{pieces.length} pieces</div>
          </div>
<div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
              <div onClick={() => setShowSettings(true)} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <IconSettings size={14} color="#a8dee0"/>
              </div>
            {searchOpen && (
              <input
                autoFocus
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search pieces..."
                style={{ background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.25)', borderRadius: '20px', padding: '6px 12px', fontSize: '12px', color: '#e8f8f6', outline: 'none', fontFamily: 'Jost, sans-serif', width: '160px' }}
              />
            )}
            <div
              onClick={() => { setSearchOpen(!searchOpen); setSearchQuery('') }}
              style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              {searchOpen ? <IconX size={14} color="#a8dee0"/> : <IconSearch size={14} color="#a8dee0"/>}
            </div>
          </div>
        </div>

        {/* Stage filter row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', overflowX: 'auto', scrollbarWidth: 'none' }}>
          <span style={{ fontSize: '9px', letterSpacing: '.8px', textTransform: 'uppercase', color: t.textMuted, flexShrink: 0, opacity: .8 }}>Stage</span>
          {['all', 'greenware', 'bisque-fire', 'glaze-fire', 'delivered'].map(s => (
            <div key={s} onClick={() => setStageFilter(s)} style={{ fontSize: '10px', padding: '4px 11px', borderRadius: '14px', whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', background: stageFilter === s ? t.accent : 'rgba(255,255,255,.1)', border: stageFilter === s ? `1px solid ${t.accentDark}` : '1px solid rgba(255,255,255,.16)', color: stageFilter === s ? t.accentText : '#c8eeec' }}>
              {s === 'all' ? 'All' : STAGE_LABELS[s]}
            </div>
          ))}
        </div>

        {/* Type filter row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflowX: 'auto', scrollbarWidth: 'none' }}>
          <span style={{ fontSize: '9px', letterSpacing: '.8px', textTransform: 'uppercase', color: t.textMuted, flexShrink: 0, opacity: .8 }}>Type</span>
          <div onClick={() => setTypeFilter('all')} style={{ fontSize: '10px', padding: '4px 11px', borderRadius: '14px', whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', background: typeFilter === 'all' ? t.accent : 'rgba(255,255,255,.1)', border: typeFilter === 'all' ? `1px solid ${t.accentDark}` : '1px solid rgba(255,255,255,.16)', color: typeFilter === 'all' ? t.accentText : '#c8eeec' }}>All</div>
          {pieceTypes.map(pt => (
            <div key={pt.id} onClick={() => setTypeFilter(pt.name)} style={{ fontSize: '10px', padding: '4px 11px', borderRadius: '14px', whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', background: typeFilter === pt.name ? t.accent : 'rgba(255,255,255,.1)', border: typeFilter === pt.name ? `1px solid ${t.accentDark}` : '1px solid rgba(255,255,255,.16)', color: typeFilter === pt.name ? t.accentText : '#c8eeec' }}>
              {pt.name}
            </div>
          ))}
        </div>
      </div>

      {/* TOOLBAR */}
      <div style={{ background: t.darkHeader, padding: '7px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '10px', fontWeight: '300', color: t.textMuted }}>{filteredPieces.length} pieces · newest first</div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {['grid', 'list'].map(v => (
            <div key={v} onClick={() => setView(v)} style={{ width: '26px', height: '26px', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: view === v ? 'rgba(255,255,255,.18)' : 'rgba(255,255,255,.07)' }}>
              {v === 'grid' ? <IconLayoutGrid size={13} color={view === v ? '#e8f8f6' : t.textMuted}/> : <IconList size={13} color={view === v ? '#e8f8f6' : t.textMuted}/>}
            </div>
          ))}
        </div>
      </div>

      {/* GRID / LIST */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: t.textLight, fontSize: '13px' }}>Loading pieces...</div>
      ) : filteredPieces.length === 0 ? (
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '28px', color: t.header, marginBottom: '8px' }}>No pieces yet</div>
          <div style={{ fontSize: '13px', color: t.textLight }}>Tap + to add your first piece</div>
        </div>
      ) : (
        <div style={view === 'grid' ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '12px 12px 24px' } : { display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px 12px 24px' }}>
          {filteredPieces.map(piece => (
            <div key={piece.id} onClick={() => setSelectedPiece(piece.id)} style={{ background: t.card, borderRadius: '14px', overflow: 'hidden', border: `1px solid ${t.border}`, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
              {view === 'grid' && (
                <div style={{ height: '96px', position: 'relative', overflow: 'visible' }}>
                  {piece.default_photo_url ? (
                    <img src={piece.default_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
                  ) : (
                    TEXTURES[(piece.texture_index || 1) - 1](t)
                  )}
                  <div style={{ position: 'absolute', top: '7px', left: '7px', fontSize: '9px', fontWeight: '500', padding: '2px 8px', borderRadius: '10px', ...getStagePillStyle(piece.firing_stage) }}>
                    {piece.firing_stage === 'delivered' ? PIZZA_SVG('#e8f8f6') : null}
                    {STAGE_LABELS[piece.firing_stage]}
                  </div>
                  <div onClick={(e) => { e.stopPropagation(); setEllipsisMenu(ellipsisMenu === piece.id ? null : piece.id) }} style={{ position: 'absolute', top: '6px', right: '8px', color: 'rgba(255,255,255,.9)', fontSize: '14px', fontWeight: '700', letterSpacing: '1px', lineHeight: 1, cursor: 'pointer', padding: '4px' }}>⋮</div>
                  {ellipsisMenu === piece.id && (
                    <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '28px', right: '6px', background: '#fff', borderRadius: '12px', border: `1px solid ${t.border}`, boxShadow: '0 4px 16px rgba(0,0,0,.12)', zIndex: 20, overflow: 'hidden', minWidth: '160px' }}>
                      {['bisque-fire', 'glaze-fire', 'delivered'].map(stage => (
                        <div key={stage} onClick={() => updatePieceStage(piece.id, stage)} style={{ padding: '10px 14px', fontSize: '12px', fontWeight: '400', color: t.text, cursor: 'pointer', borderBottom: `1px solid ${t.border}` }}>
                          Move to {stage === 'delivered' ? '🍕 Delivered' : stage}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{ padding: view === 'grid' ? '8px 10px 10px' : '10px 12px', display: 'flex', alignItems: view === 'list' ? 'center' : 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontFamily: 'Alike, serif', fontSize: '11px', color: t.text, lineHeight: 1.3, marginBottom: '2px' }}>{piece.unique_id}</div>
                  <div style={{ fontSize: '9px', fontWeight: '300', color: t.textLight }}>{new Date(piece.date_created).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                </div>
                {view === 'list' && (
                  <div style={{ fontSize: '9px', fontWeight: '500', padding: '2px 8px', borderRadius: '10px', flexShrink: 0, ...getStagePillStyle(piece.firing_stage) }}>
                    {STAGE_LABELS[piece.firing_stage]}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB */}
<div onClick={() => setShowNewPiece(true)} style={{ position: 'fixed', bottom: '80px', right: 'calc(50% - 215px + 16px)', width: '52px', height: '52px', borderRadius: '50%', background: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 12px rgba(0,0,0,.2)', zIndex: 10 }}>        <IconPlus size={26} color={t.accentText}/>
      </div>

      {/* BOTTOM NAV */}
      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '430px', background: t.card, borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-around', padding: '9px 0 18px', zIndex: 10 }}>
        {[
          { id: 'library', icon: <IconLayoutGrid size={20}/>, label: 'Library' },
          { id: 'bisqueware', icon: <IconFlame size={20}/>, label: 'Bisqueware' },
          { id: 'glazes', icon: <IconColorSwatch size={20}/>, label: 'Glazes' },
        ].map(item => (
          <div key={item.id} onClick={() => setActiveTab(item.id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: 'pointer', color: activeTab === item.id ? t.header : t.textLight }}>
            {item.icon}
            <span style={{ fontSize: '9px', fontWeight: '400' }}>{item.label}</span>
          </div>
        ))}
      </div>
      {ellipsisMenu && (
        <div onClick={() => setEllipsisMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 15 }}/>
      )}
      {showNewPiece && (
  <NewPieceModal
    onClose={() => setShowNewPiece(false)}
    onCreated={(piece) => { setPieces(prev => [piece, ...prev]); setShowNewPiece(false) }}
    theme={t}
  />
)}
      {showSettings && (
  <Settings
    theme={t}
    currentTheme={theme}
    onThemeChange={(newTheme) => { setTheme(newTheme); setShowSettings(false) }}
    onClose={() => setShowSettings(false)}
  />
)}
    </div>
  )
}